import { Button, Spinner } from "@heroui/react";
import HintTooltip from "@/components/common/HintTooltip";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, History, Upload } from "lucide-react";
import { useEffect, useState } from "react";

import { getDrawingRevisionUrl, listDrawingRevisions } from "@/features/drawings/api";
import DrawingRevisionsDialog from "./DrawingRevisionsDialog";
import DrawingUploadDialog from "./DrawingUploadDialog";

/**
 * 明細の見積画面の左半分。図面 PDF の閲覧と差し替えを担う。
 *
 * 工程ごとの金額は図面を見ないと決められないので、**図面を出したまま右半分で
 * 金額を入れられる**ようにするためのもの。図面は縦いっぱいに出し、登録や履歴は
 * ダイアログに追い出してある（入力欄を常に置くと、肝心の図面が小さくなるため）。
 *
 * PDF 自体はブラウザ内蔵のビューアに任せる（iframe に presigned URL を渡す）。
 * API はファイル本体を中継しないので、描画も S3 から直接行われる。URL は開く
 * 直前に取る。presigned URL には期限があり（既定 15 分）、持ち回ると切れるため。
 *
 * **図面はまだ 1 枚も無いことがある**（``drawingId`` が null）。明細を足しただけ
 * では図面を作らないため（[[drawings]]）。最初の PDF を上げるときに
 * ``onCreateDrawing`` で作ってもらう。
 */
export default function ItemDrawingPanel({
  drawingId,
  drawingNumber,
  itemName,
  canWrite,
  isResizing,
  onChanged,
  onCreateDrawing,
}: {
  /** この明細の代表の図面。まだ 1 枚も無ければ null。 */
  drawingId: number | null;
  drawingNumber: string;
  itemName: string;
  canWrite: boolean;
  /** 境目をドラッグ中。iframe がカーソルを吸わないよう無効化する。 */
  isResizing: boolean;
  /** 版が増減したときに呼ぶ（明細一覧の版数を引き直すため）。 */
  onChanged: () => void;
  /** 図面が 1 枚も無いときに 1 枚作る。作られた図面 ID を返す。 */
  onCreateDrawing: () => Promise<number>;
}) {
  const [revisionId, setRevisionId] = useState<number | null>(null);
  // アップロード先の図面。既にあればそれ、無ければ押したときに作る。
  const [uploadTarget, setUploadTarget] = useState<number | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const revisions = useQuery({
    queryKey: ["drawing-revisions", drawingId],
    queryFn: () => listDrawingRevisions(drawingId as number),
    enabled: drawingId != null,
  });

  /** 「図面を更新」「図面を登録」の入口。図面が無ければ先に 1 枚作る。 */
  const startUpload = async () => {
    if (drawingId != null) {
      setUploadTarget(drawingId);
      return;
    }
    setPreparing(true);
    try {
      setUploadTarget(await onCreateDrawing());
    } finally {
      setPreparing(false);
    }
  };
  const items = revisions.data?.items ?? [];

  // 図面が変わったとき・版が増えたときは最新の版（一覧の先頭）を出す。
  useEffect(() => {
    setRevisionId(items[0]?.drawing_revision_id ?? null);
    // items の配列同一性ではなく中身で判定する（再取得のたびに開き直さない）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingId, items[0]?.drawing_revision_id]);

  const url = useQuery({
    queryKey: ["drawing-revision-url", drawingId, revisionId],
    queryFn: () => getDrawingRevisionUrl(drawingId as number, revisionId as number),
    enabled: drawingId != null && revisionId != null,
    // 期限（既定 15 分）より短い間だけ使い回す。
    staleTime: 10 * 60 * 1000,
  });

  const selected = items.find((r) => r.drawing_revision_id === revisionId);

  const refresh = async () => {
    await revisions.refetch();
    onChanged();
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="truncate font-bold text-default-700">{drawingNumber}</span>
        <span className="truncate text-small text-default-500">{itemName}</span>
        {/* 版が複数あるときは、いま見ている版を添える（切り替えは履歴から）。 */}
        {selected && items.length > 1 && (
          <span className="shrink-0 text-small text-default-500">
            版 {selected.revision}
          </span>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {canWrite && (
            <Button
              size="sm"
              radius="full"
              variant="bordered"
              data-testid="drawing-update"
              startContent={<Upload size={14} />}
              isLoading={preparing}
              onPress={() => void startUpload()}
            >
              図面を更新
            </Button>
          )}
          <HintTooltip content="更新履歴">
            <Button
              isIconOnly
              size="sm"
              radius="full"
              variant="light"
              aria-label="更新履歴"
              data-testid="drawing-history"
              isDisabled={drawingId == null}
              onPress={() => setHistoryOpen(true)}
            >
              <History size={16} className="text-brand-500" />
            </Button>
          </HintTooltip>
          {url.data && (
            <Button
              as="a"
              href={url.data}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              radius="full"
              variant="bordered"
              startContent={<ExternalLink size={14} />}
            >
              新しいタブで開く
            </Button>
          )}
        </div>
      </div>

      {/* 版が 1 件も無いときは URL を取りに行かない。先に未登録を判定しないと、
          止まったままの問い合わせを待ち続けて読み込み中から変わらなくなる。 */}
      <div className="min-h-0 flex-1">
        {drawingId != null && revisions.isPending ? (
          <div className="flex h-full items-center justify-center">
            <Spinner label="読み込み中..." />
          </div>
        ) : items.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-default-300">
            <p className="text-sm text-default-500">まだ図面が登録されていません。</p>
            {canWrite && (
              <Button
                color="primary"
                radius="full"
                startContent={<Upload size={16} />}
                isLoading={preparing}
                onPress={() => void startUpload()}
              >
                図面を登録
              </Button>
            )}
          </div>
        ) : url.isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner label="読み込み中..." />
          </div>
        ) : url.isError ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <p className="text-default-600">PDF を表示できませんでした。</p>
            <Button color="primary" onPress={() => void url.refetch()}>
              再読み込み
            </Button>
          </div>
        ) : (
          <iframe
            // 版を切り替えたら読み直す。
            key={revisionId ?? "none"}
            src={url.data}
            title={`${drawingNumber} 版 ${selected?.revision ?? ""}`}
            className={[
              "h-full w-full rounded-md border border-default-200",
              // ドラッグ中は iframe に mousemove を吸わせない。
              isResizing ? "pointer-events-none" : "",
            ].join(" ")}
          />
        )}
      </div>

      <DrawingUploadDialog
        isOpen={uploadTarget != null}
        drawingId={uploadTarget ?? 0}
        drawingNumber={drawingNumber}
        isFirst={items.length === 0}
        onClose={() => setUploadTarget(null)}
        onUploaded={() => void refresh()}
      />

      <DrawingRevisionsDialog
        isOpen={historyOpen}
        drawingId={drawingId}
        drawingNumber={drawingNumber}
        itemName={itemName}
        canWrite={canWrite}
        onClose={() => setHistoryOpen(false)}
        onChanged={() => void refresh()}
      />
    </div>
  );
}
